import React, { useCallback, useMemo, useState } from 'react'
import { useT } from '@transifex/react'
import type { MenuProps } from 'antd'
import { useLocation } from 'react-router-dom'
import { Menu, ConfigProvider } from 'src/antd'
import styled, { css } from 'styled-components'

import { useRoutingHistory } from 'src/reducers/routing/child-router-factory'
import {
  HISTORY_STORE_ACTIONS_KEY,
  RESET_HISTORY,
} from 'src/reducers/routing/store'
import { gray200, blue100, blue200, navy900 } from 'src/styles/color-theme'

import getItems from './getItems'
import SearchBox from '../SearchBox'

type NavigationMenuProps = {
  openedKey?: string
  collapsed?: boolean
  onToggleSidebar?: () => void
}

// DO NOT TOUCH - This style is used globally across the application
export const SubMenuStyle = css`
  .ant-menu-submenu-inline,
  .ant-menu-submenu-vertical {
    padding: 0 6px;
  }
  .ant-menu-item {
    padding: 0 6px;
    margin: 0 6px !important;
    width: auto !important;
  }
  .ant-menu-inline-collapsed > .ant-menu-item {
    padding-inline: calc(50% - 16px);
  }
  .ant-menu-submenu-popup {
    .ant-menu {
      background-color: ${(p) => p.theme.gray200} !important;
      padding: 6px 0 !important;
    }
  }
  .ant-menu-submenu-selected > .ant-menu-submenu-title {
    background-color: ${(p) => p.theme.blue200} !important;
    color: ${(p) => p.theme.navy900} !important;
  }
`

const SidebarContainer = styled.div`
  height: calc(100vh - 57px - 70px); // -70px for the Intercom snippet icon
  display: flex;
  flex-direction: column;
`

const SidebarMenuWrapper = styled.div`
  flex: 1 1 auto;
  overflow-x: hidden;
`

const menuThemeConfig = {
  hashed: false,
  components: {
    Menu: {
      itemSelectedBg: blue200,
      itemSelectedColor: navy900,
      itemActiveBg: blue100,
      itemHoverBg: blue100,
      popupBg: gray200,
      itemBorderRadius: 6,
      subMenuItemBorderRadius: 6,
      itemPaddingInline: 0,
      iconSize: 18,
      collapsedIconSize: 20,
      itemColor: navy900,
      itemHoverColor: navy900,
      borderInlineEnd: 0,
      itemHeight: 40,
      collapsedWidth: 64,
    },
  },
}

type AntdMenuItem = NonNullable<MenuProps['items']>[number]

interface MenuItemWithLink {
  key: string
  link?: string
  children?: MenuItemWithLink[]
}

interface MenuItemWithSkipLinks {
  link?: string
  skipLinks?: string[]
}

const findBestMatchKey = (
  path: string,
  items: AntdMenuItem[]
): string | undefined => {
  const candidates: { key: string; link: string }[] = []

  const addCandidate = (key: string, link: string) =>
    candidates.push({ key, link })

  const processItem = (item: unknown) => {
    if (!item || typeof item !== 'object' || !('key' in item) || !item.key)
      return

    const menuItem = item as MenuItemWithLink
    const key = String(menuItem.key)
    const link = typeof menuItem.link === 'string' ? menuItem.link : key
    addCandidate(key, link)

    if (Array.isArray(menuItem.children)) {
      menuItem.children.forEach(processItem)
      menuItem.children.forEach((child) => {
        const childWithLinks = child as MenuItemWithSkipLinks
        if (childWithLinks.link) {
          addCandidate(key, childWithLinks.link)
        }
        if (Array.isArray(childWithLinks.skipLinks)) {
          childWithLinks.skipLinks.forEach((skipLink: string) =>
            addCandidate(key, skipLink)
          )
        }
      })
    }
  }

  items.forEach(processItem)

  const matches = candidates.filter(({ key, link }) =>
    [key, link].some(
      (target) =>
        path.startsWith(target) || path === target || path.includes(target)
    )
  )

  return matches.sort(
    (a, b) =>
      Math.max(b.key.length, b.link.length) -
      Math.max(a.key.length, a.link.length)
  )[0]?.key
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({
  openedKey,
  collapsed,
  onToggleSidebar,
}) => {
  const t = useT()
  const [history, { roles }] = useRoutingHistory()
  const { pathname } = useLocation()
  const [openKeys, setOpenKeys] = useState<string[]>([])

  const handleLinkClick = useCallback(
    (path: string, state?: Record<string, unknown>) => {
      history.push(path, {
        ...state,
        [HISTORY_STORE_ACTIONS_KEY]: [RESET_HISTORY],
      })
    },
    [history]
  )

  const menuItems = useMemo(
    () => getItems(t, roles, handleLinkClick),
    [t, roles, handleLinkClick]
  )
  const validMenuItems = useMemo(
    () => menuItems.filter(Boolean) as AntdMenuItem[],
    [menuItems]
  )

  const selectedKeys = useMemo(() => {
    if (openedKey) return [openedKey]
    const bestMatch = findBestMatchKey(pathname, validMenuItems)
    return bestMatch ? [bestMatch] : []
  }, [openedKey, pathname, validMenuItems])

  const handleOpenChange = useCallback(
    (keys: string[]) => setOpenKeys(keys),
    []
  )
  const handleMenuOpen = useCallback(
    (parentKey: string) => setOpenKeys([parentKey]),
    []
  )

  return (
    <SidebarContainer>
      <SearchBox
        menuItems={validMenuItems}
        onMenuOpen={handleMenuOpen}
        collapsed={collapsed}
        onToggleSidebar={onToggleSidebar}
      />
      <SidebarMenuWrapper>
        <ConfigProvider theme={menuThemeConfig}>
          <Menu
            mode="inline"
            items={menuItems}
            selectedKeys={selectedKeys}
            openKeys={openKeys}
            onOpenChange={handleOpenChange}
          />
        </ConfigProvider>
      </SidebarMenuWrapper>
    </SidebarContainer>
  )
}

export default NavigationMenu
